import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { TrackerMilestonesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrackerMilestoneActions from "../../../actions/trackerMilestones";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { QALookupsLayout } from "../../index";

import "./TrackerMilestonesPage.scss";

interface TrackerMilestonesPageProps {
    formFilter: IFormFilter;
    trackerMilestones: Dtos.TrackerMilestone[];
    loadingTrackerMilestones: boolean;
    loadTrackerMilestonesSuccess: boolean;
    loadTrackerMilestonesFailure: boolean;
    loadTrackerMilestonesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearTrackerMilestones: TrackerMilestoneActions.ITrackerMilestoneClearActionCreator;
    loadTrackerMilestones: TrackerMilestoneActions.ITrackerMilestoneLoadActionCreator;
    filter: TrackerMilestoneActions.ITrackerMilestoneFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const TrackerMilestonesPage: FC<TrackerMilestonesPageProps> = ({
    formFilter,
    trackerMilestones,
    loadingTrackerMilestones,
    loadTrackerMilestonesSuccess,
    loadTrackerMilestonesFailure,
    loadTrackerMilestonesErrorCode,
    permissions,
    pageSize,
    clearTrackerMilestones,
    loadTrackerMilestones,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showTrackerMilestones =
        !loadingTrackerMilestones && loadTrackerMilestonesSuccess;


    // Load tracker milestones
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(TrackerMilestonesBreadcrumbs());

            loadTrackerMilestones();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        TrackerMilestonesBreadcrumbs,
        loadTrackerMilestones,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrackerMilestonesFailure) {
            navigate(`/error/${loadTrackerMilestonesErrorCode}`);
        }
    }, [
        loadTrackerMilestonesFailure,
        loadTrackerMilestonesErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the tracker milestone on component unmount
    useEffect(() => {
        return () => {
            clearTrackerMilestones();
        };
    }, [clearTrackerMilestones]);

    const createTable = useMemo(() => {
        if (!trackerMilestones || trackerMilestones.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently tracker milestones to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = trackerMilestones.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={trackerMilestones}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : trackerMilestones.length
                    }
                    pageSize={
                        showPagination ? pageSize : trackerMilestones.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no tracker milestones to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string =
                                    "/settings/qa/trackermilestones/" +
                                    rowInfo.original.id;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        trackerMilestones,
        PAGE_SIZE_OPTIONS,
        pageSize,
        DEFAULT_PAGE_SIZE,
        navigate,
        setPageSize,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Tracker Milestones</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TaskTypesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/qa/trackermilestones/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTrackerMilestones ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrackerMilestones ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showTrackerMilestones, createTable]);

    return (
        <QALookupsLayout
            loading={loadingTrackerMilestones}
            permission={Dtos.Permission.TaskTypesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrackerMilestonesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.trackerMilestones.formFilter,
        trackerMilestones: state.trackerMilestones.formFilteredData,
        loadingTrackerMilestones:
            state.trackerMilestones.loadState &&
            state.trackerMilestones.loadState.status == RequestState.Pending,
        loadTrackerMilestonesSuccess:
            state.trackerMilestones.loadState &&
            state.trackerMilestones.loadState.status == RequestState.Success,
        loadTrackerMilestonesFailure:
            state.trackerMilestones.loadState &&
            state.trackerMilestones.loadState.status == RequestState.Failure,
        loadTrackerMilestonesErrorCode:
            state.trackerMilestones.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrackerMilestonesPageProps> => {
    return {
        loadTrackerMilestones: bindActionCreators(
            TrackerMilestoneActions.LoadTrackerMilestone,
            dispatch
        ),
        clearTrackerMilestones: bindActionCreators(
            TrackerMilestoneActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(TrackerMilestoneActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrackerMilestonesPage);
